import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
  /** Empty string = the "nothing selected" row. */
  id: string;
  label: string;
}

/**
 * A single-select listbox that stays inside the box it is drawn in (#18593).
 *
 * <p><b>Why this exists instead of `&lt;select&gt;`.</b> A native select's option popup is painted
 * by the browser, sized to its longest option and laid out outside the page — so in the 320px
 * exam sidebar a long name spills across the screen, and no CSS on the page can stop it:
 * `text-overflow` does not apply to an `&lt;option&gt;` in the popup, and `title` on an
 * `&lt;option&gt;` is not rendered as a tooltip. The popup has to be ours to obey the rule QA
 * asked for — one truncated line, full text on hover.
 *
 * <p>Extracted from {@code DepartmentSelect}, which solved this for Khoa across four rounds of
 * this ticket. Phòng, Bác sĩ and Dịch vụ sit in the same sidebar with the same widths and had the
 * same native control, so they had the same bug waiting; sharing the component is what stops them
 * drifting apart again.
 *
 * <p>Keyboard behaviour that the native control gave for free is re-implemented on purpose —
 * arrows, Home/End, Enter/Space, Esc — rather than quietly lost.
 */
export function OptionCombobox({
  options,
  value,
  onChange,
  id,
  disabled,
  className,
}: {
  options: ComboboxOption[];
  /** "" or null = the empty row. */
  value: string | null;
  onChange: (id: string) => void;
  id?: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedIndex = useMemo(
    () => Math.max(0, options.findIndex((o) => o.id === (value ?? ""))),
    [options, value]
  );
  const selectedLabel = options[selectedIndex]?.label ?? "";

  useEffect(() => {
    if (open) setActive(selectedIndex);
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // Keep the highlighted row in view when arrowing through a long list.
  useEffect(() => {
    if (!open) return;
    listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  function commit(index: number) {
    onChange(options[index]?.id ?? "");
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      case "ArrowDown":
        e.preventDefault();
        setActive((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        e.preventDefault();
        setActive(0);
        break;
      case "End":
        e.preventDefault();
        setActive(options.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        commit(active);
        break;
      default:
        break;
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        // The closed field truncates too, so hover still reveals the full name.
        title={selectedLabel}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input",
          "bg-background px-3 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
      >
        {/* min-w-0 is what lets the truncation actually happen inside a flex row. */}
        <span className="min-w-0 flex-1 truncate">{selectedLabel}</span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        // Absolutely positioned and w-full: the list can never be wider than the field, which is
        // the overflow QA reported. z-30 keeps it above sibling cards.
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-background py-1 shadow-md"
        >
          {options.map((o, i) => (
            <li key={o.id || "none"}>
              <button
                type="button"
                role="option"
                aria-selected={i === selectedIndex}
                title={o.label}
                onMouseEnter={() => setActive(i)}
                onClick={() => commit(i)}
                className={cn(
                  "block w-full truncate px-3 py-1.5 text-left text-sm",
                  i === active && "bg-muted",
                  i === selectedIndex && "font-medium text-foreground"
                )}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
